import {
	CategoryVideos,
	FilterableListController,
} from "../components/VideosList";
import { MotionMain } from "../MotionMain";
import { standardizeSubCategoryPath, usePlayerData } from "../usePlayerData";
import _ from "lodash";
import { useEffect, useMemo } from "react";
import { useLocation, useNavigate } from "react-router-dom";
import { resolveSubCategoryPathIds } from "../utils/subCategoryPath";

export type FilterByGroup = {
	groupName?: string;
	groupId: string;
};

export const CategoryRender = (props: {
	name: string;
	suffixButton?: JSX.Element;
	categoryId?: string;
	subCategoryPathIds?: string[];
	groupFilter: FilterByGroup;
}) => {
	const playerData = usePlayerData();
	const location = useLocation();
	const navigate = useNavigate();
	const category = playerData.queryCategoryBy({ 
		groupId: props.groupFilter.groupId, 
		categoryId: props.categoryId,
		categoryName: props.name,
	});
	const selectedIds = props.subCategoryPathIds || [];
	const rawLeafId = selectedIds[selectedIds.length - 1];
	const resolvedPath = useMemo(
		() => resolveSubCategoryPathIds(category?.subCategories, selectedIds),
		[category?.subCategories, selectedIds],
	);
	const selectedLeafId = resolvedPath.ids[resolvedPath.ids.length - 1];
	const subCategory = playerData.querySubCategoryBy({ 
		groupId: props.groupFilter.groupId, 
		categoryId: props.categoryId,
		categoryName: props.name,
		subCategoryId: selectedLeafId || rawLeafId,
	});

	const selectedNames = useMemo(() => {
		if (!category || resolvedPath.ids.length === 0) return [];

		const names: string[] = [];
		let currentList = category.subCategories;

		for (const id of resolvedPath.ids) {
			const found = currentList.find((item) => item.id === id);
			if (!found) break;
			names.push(found.name);
			currentList = found.subCategories || [];
		}

		return names;
	}, [category, resolvedPath.ids]);

	const breadcrumb = _.uniq([
		props.name,
		category?.name || "",
		...selectedNames,
		subCategory?.name,
	]).filter(Boolean) as string[];
	const subCategoryPaths = category
		? [
				standardizeSubCategoryPath([category.name]),
				selectedNames.length > 0
					? standardizeSubCategoryPath([category.name, ...selectedNames])
					: null,
		  ].filter(Boolean) as string[]
		: undefined;
	const normalizedPath = resolvedPath.ids.join("/");
	const rawPath = selectedIds.join("/");
	const basePath = useMemo(() => {
		const segments = location.pathname.split("/").filter(Boolean);
		const categoryIndex = segments.indexOf(props.categoryId || "");
		if (categoryIndex === -1) return location.pathname;
		return `/${segments.slice(0, categoryIndex + 1).join("/")}`;
	}, [location.pathname, props.categoryId]);

	useEffect(() => {
		if (resolvedPath.isValid || resolvedPath.ids.length === 0) {
			if (!resolvedPath.isValid && rawPath) {
				navigate(basePath, { replace: true });
			}
			return;
		}

		if (normalizedPath !== rawPath) {
			navigate(`${basePath}/${normalizedPath}`, { replace: true });
		}
	}, [basePath, navigate, normalizedPath, rawPath, resolvedPath.ids.length, resolvedPath.isValid]);

	return (
		<MotionMain key="/treinamento/category">
			<FilterableListController
				groupFilter={props.groupFilter}
				breadcrumb={breadcrumb}
				categoryId={props.categoryId}
				subCategoryId={selectedLeafId || rawLeafId}
				subCategoryPaths={subCategoryPaths}
			>
				<CategoryVideos
					categoryId={props.categoryId}
					groupFilter={props.groupFilter}
					breadcrumb={breadcrumb}
					suffixButton={props.suffixButton}
					subCategoryPathIds={resolvedPath.ids}
				/>
			</FilterableListController>
		</MotionMain>
	);
};
